"use client"

import React, { useEffect, useMemo, useState } from "react"
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog"
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert"
import { Button } from "@/components/ui/button"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table"
import { Loader2, Plus, Settings2, Trash2, Webhook } from "lucide-react"
import { Blade, PillButton } from "@/components/blades/blade"
import { HubCard, StatusPill, ToolHeader, ToolbarPill, hubTh, hubTr } from "@/components/hub/hub-canvas"
import { toast } from "sonner"
import { useAtsSources, type AtsSourceInfo } from "@/hooks/use-ats-sources"
import { useEventSync, type AtsVendorConfigurationInfo } from "@/hooks/use-event-sync"
import { useTranslation } from "@/hooks/use-translation"
import type { TranslationKey } from "@/lib/translations"
import { getApiErrorDetail } from "@/lib/api"
import { createDefaultDocument, setPath } from "./doc-paths"
import { EventSyncEditor } from "./event-sync-editor"

/**
 * Admin management of the ATS event sync processor's per-source configuration
 * (AtsVendorConfiguration documents): which events sync, status mappings, and
 * the business rules applied when placements become FBO work orders. The
 * processor re-reads configuration on a ~60 second cache, so edits apply
 * without a function restart. Webhook identities (Bullhorn subscription names,
 * Loxo agency slugs, JobAdder webhook ids) stay ops-managed in the function
 * app settings.
 */
export function EventSyncPage() {
  const { t } = useTranslation()
  const { loadSources } = useAtsSources()
  const { loadConfigurations, saveConfiguration, deleteConfiguration } = useEventSync()

  const [sources, setSources] = useState<AtsSourceInfo[]>([])
  const [configurations, setConfigurations] = useState<AtsVendorConfigurationInfo[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  // Editor state: null = closed; id null = creating
  const [editing, setEditing] = useState<{ id: string | null; doc: Record<string, any> } | null>(null)
  const [isSaving, setIsSaving] = useState(false)
  const [pendingDelete, setPendingDelete] = useState<AtsVendorConfigurationInfo | null>(null)
  const [isDeleting, setIsDeleting] = useState(false)

  // Create flow: pick the source (and optionally a document to copy) first
  const [createSourceCode, setCreateSourceCode] = useState("")
  const [copyFromId, setCopyFromId] = useState<string>("")
  const [createOpen, setCreateOpen] = useState(false)

  // Dismissing the form forgets the source and "copy from" choice, so the next open starts from defaults
  const closeCreate = () => {
    setCreateOpen(false)
    setCreateSourceCode("")
    setCopyFromId("")
  }

  const refresh = async () => {
    const [loadedSources, loadedConfigurations] = await Promise.all([loadSources(), loadConfigurations()])
    setSources(loadedSources)
    setConfigurations(loadedConfigurations)
  }

  /** An API error's own detail if it carries one, otherwise the localized fallback for the operation. */
  const errorMessage = (err: unknown, fallbackKey: TranslationKey) =>
    getApiErrorDetail(err) ?? (err instanceof Error ? err.message : t(fallbackKey))

  useEffect(() => {
    refresh()
      .catch((err) => setError(errorMessage(err, "eventSync.errors.loadFailed")))
      .finally(() => setIsLoading(false))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const activeSource = useMemo(() => sources.find((s) => s.isActive) ?? null, [sources])

  // Newest doc per source wins in the processor; older same-source docs are shadowed
  const shadowedIds = useMemo(() => {
    const seen = new Set<string>()
    const shadowed = new Set<string>()
    for (const config of configurations) {
      const key = (config.atsSource ?? "").toLowerCase()
      if (!key) continue
      if (seen.has(key)) shadowed.add(config.id ?? "")
      else seen.add(key)
    }
    return shadowed
  }, [configurations])

  const activeHasConfiguration = useMemo(
    () =>
      activeSource !== null &&
      configurations.some(
        (c) => !shadowedIds.has(c.id ?? "") && (c.atsSource ?? "").toLowerCase() === activeSource.code.toLowerCase()
      ),
    [activeSource, configurations, shadowedIds]
  )

  const sourcesWithoutConfiguration = useMemo(
    () =>
      sources.filter(
        (source) => !configurations.some((c) => (c.atsSource ?? "").toLowerCase() === source.code.toLowerCase())
      ),
    [sources, configurations]
  )

  const sourceForCode = (code: string | null) =>
    code ? sources.find((s) => s.code.toLowerCase() === code.toLowerCase()) ?? null : null

  // Preselect the first unconfigured source VISIBLY (in the Select) rather than
  // silently defaulting at click time — the admin must see which source the new
  // configuration will bind to
  useEffect(() => {
    if (!createSourceCode && sourcesWithoutConfiguration.length > 0) {
      setCreateSourceCode(sourcesWithoutConfiguration[0].code)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sourcesWithoutConfiguration])

  const startCreate = () => {
    if (!createSourceCode) return

    if (copyFromId) {
      const template = configurations.find((c) => c.id === copyFromId)
      if (template) {
        // setPath reuses a legacy PascalCase AtsSource property instead of
        // appending a second, differently-cased one
        const copied = setPath(JSON.parse(JSON.stringify(template.data)), ["atsSource"], createSourceCode)
        setEditing({ id: null, doc: copied })
        return
      }
    }

    setEditing({ id: null, doc: createDefaultDocument(createSourceCode) })
  }

  const handleSave = async (doc: Record<string, any>) => {
    if (!editing) return
    setIsSaving(true)
    try {
      await saveConfiguration(editing.id, doc)
    } catch (err) {
      toast.error(errorMessage(err, "eventSync.errors.saveFailed"))
      return
    } finally {
      setIsSaving(false)
    }
    // The save succeeded — a reload hiccup after this must not surface as a save failure
    toast.success(t((editing.id ? "eventSync.toasts.saved" : "eventSync.toasts.created")))
    setEditing(null)
    setCopyFromId("")
    setCreateSourceCode("")
    await refresh().catch(() => setError(t("eventSync.errors.savedReloadFailed")))
  }

  const handleDelete = async () => {
    if (!pendingDelete?.id) return
    const deleted = pendingDelete
    setIsDeleting(true)
    try {
      await deleteConfiguration(deleted.id!)
    } catch (err) {
      toast.error(errorMessage(err, "eventSync.errors.deleteFailed"))
      return
    } finally {
      setIsDeleting(false)
    }
    toast.success(
      t("eventSync.toasts.deleted", { source: deleted.atsSource ?? deleted.id ?? "" })
    )
    setPendingDelete(null)
    await refresh().catch(() => setError(t("eventSync.errors.deletedReloadFailed")))
  }

  const editingSource = editing ? sourceForCode((editing.doc?.atsSource as string) ?? null) : null

  return (
    <div className="p-4">
      <ToolHeader
        title={t("eventSync.page.title")}
        description={
          activeSource
            ? t("eventSync.page.descriptionActiveSource", { code: activeSource.code })
            : t("eventSync.page.descriptionNoActiveSource")
        }
        actions={
          <ToolbarPill variant="primary" onClick={() => setCreateOpen(true)} icon={<Plus className="h-3.5 w-3.5" />} disabled={sources.length === 0}>
            {t("eventSync.page.newConfiguration")}
          </ToolbarPill>
        }
      />

      <Blade
        open={editing !== null}
        onClose={() => {
          if (!isSaving) setEditing(null)
        }}
        size="xl"
        eyebrow={editingSource?.code ?? t("eventSync.blade.eyebrow")}
        title={
          editing?.id
            ? t("eventSync.editor.bladeTitleEdit", { code: editingSource?.code ?? "" })
            : t("eventSync.editor.bladeTitleNew")
        }
        description={t("eventSync.editor.bladeDescription")}
      >
        {editing && (
          <EventSyncEditor
            initialDoc={editing.doc}
            isNew={editing.id === null}
            sources={sources}
            activeSourceCode={activeSource?.code ?? null}
            isSaving={isSaving}
            onSave={handleSave}
            onCancel={() => setEditing(null)}
          />
        )}
      </Blade>

      {error && (
        <Alert variant="destructive" className="mb-4">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {!isLoading && activeSource && !activeHasConfiguration && (
        <Alert className="mb-4 border-amber-500 text-amber-700">
          <Settings2 className="h-4 w-4" />
          <AlertTitle>{t("eventSync.warnings.noConfigTitle")}</AlertTitle>
          <AlertDescription>
            {t("eventSync.warnings.noConfigBody", { code: activeSource.code })}
          </AlertDescription>
        </Alert>
      )}

      <HubCard className="mb-4">
        {isLoading ? (
          <div className="flex items-center px-4 py-6 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin mr-2" /> {t("common.state.loading")}
          </div>
        ) : configurations.length > 0 ? (
            <Table>
              <TableHeader>
                <TableRow className="hover:bg-transparent">
                  <TableHead className={hubTh}>{t("eventSync.table.source")}</TableHead>
                  <TableHead className={hubTh}>{t("eventSync.table.atsSystem")}</TableHead>
                  <TableHead className={hubTh}>{t("eventSync.table.sourceId")}</TableHead>
                  <TableHead className={hubTh}>{t("eventSync.table.lastModified")}</TableHead>
                  <TableHead className={hubTh}>{t("eventSync.table.status")}</TableHead>
                  <TableHead className={`${hubTh} w-32`}></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {configurations.map((config) => {
                  const source = sourceForCode(config.atsSource)
                  const isShadowed = shadowedIds.has(config.id ?? "")
                  const isActiveBinding =
                    !isShadowed &&
                    activeSource !== null &&
                    (config.atsSource ?? "").toLowerCase() === activeSource.code.toLowerCase()
                  return (
                    <TableRow key={config.id ?? config.atsSource ?? Math.random()} className={hubTr}>
                      <TableCell className="font-mono text-xs font-semibold">{config.atsSource ?? "—"}</TableCell>
                      <TableCell className="text-sm">{config.atsSourceName ?? "—"}</TableCell>
                      <TableCell className="text-sm">{config.atsSourceId ?? "—"}</TableCell>
                      <TableCell className="text-xs text-muted-foreground">
                        {config.lastModifiedDateTime ? new Date(config.lastModifiedDateTime).toLocaleString() : "—"}
                      </TableCell>
                      <TableCell>
                        <div className="flex flex-wrap gap-1">
                          {isActiveBinding && (
                            <StatusPill tone="active">● {t("eventSync.table.activeSource")}</StatusPill>
                          )}
                          {!source && config.atsSource && (
                            <StatusPill tone="danger">{t("eventSync.table.unknownSource")}</StatusPill>
                          )}
                          {!config.atsSource && (
                            <StatusPill tone="danger">{t("eventSync.table.noSourceBinding")}</StatusPill>
                          )}
                          {isShadowed && (
                            <StatusPill tone="warning">{t("eventSync.table.shadowed")}</StatusPill>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex justify-end gap-1">
                          <ToolbarPill className="h-7 px-2.5 text-xs" onClick={() => setEditing({ id: config.id, doc: JSON.parse(JSON.stringify(config.data)) })}>
                            {t("eventSync.table.edit")}
                          </ToolbarPill>
                          <Button variant="ghost" size="sm" className="h-7" title={t("eventSync.table.deleteTitle")} onClick={() => setPendingDelete(config)}>
                            <Trash2 className="h-3.5 w-3.5" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  )
                })}
              </TableBody>
            </Table>
        ) : (
          <p className="px-4 py-8 text-center text-sm text-muted-foreground">
            {t("eventSync.table.empty")}
          </p>
        )}
      </HubCard>

      <Blade
        open={createOpen}
        onClose={closeCreate}
        size="sm"
        eyebrow={t("eventSync.blade.eyebrow")}
        title={t("eventSync.create.title")}
        description={t("eventSync.create.description")}
        footer={
          <>
            <PillButton variant="ghost" onClick={closeCreate}>{t("common.actions.cancel")}</PillButton>
            <PillButton onClick={() => { setCreateOpen(false); startCreate() }} disabled={!createSourceCode}>
              <Plus className="h-3.5 w-3.5" /> {t("eventSync.create.submit")}
            </PillButton>
          </>
        }
      >
        <div className="space-y-4">
          <div className="grid grid-cols-1 gap-4">
            <div className="space-y-1.5">
              <span className="text-sm font-medium">{t("eventSync.create.atsSource")}</span>
              <Select value={createSourceCode} onValueChange={setCreateSourceCode}>
                <SelectTrigger>
                  <SelectValue placeholder={t("eventSync.create.selectSourcePlaceholder")} />
                </SelectTrigger>
                <SelectContent>
                  {sources.map((source) => (
                    <SelectItem key={source.code} value={source.code}>
                      {source.code} ({source.atsSystem ?? "?"}){source.isActive ? t("eventSync.source.activeSuffix") : ""}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {sourcesWithoutConfiguration.length > 0 && (
                <p className="text-xs text-muted-foreground">
                  {t("eventSync.create.withoutConfiguration", {
                    codes: sourcesWithoutConfiguration.map((s) => s.code).join(", "),
                  })}
                </p>
              )}
            </div>
            <div className="space-y-1.5">
              <span className="text-sm font-medium">{t("eventSync.create.copyFrom")}</span>
              <Select value={copyFromId || "none"} onValueChange={(v) => setCopyFromId(v === "none" ? "" : v)}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">{t("eventSync.create.startFromDefaults")}</SelectItem>
                  {configurations
                    .filter((c) => c.id)
                    .map((config) => (
                      <SelectItem key={config.id!} value={config.id!}>
                        {t("eventSync.create.copyOption", { name: config.atsSource ?? config.id })}
                      </SelectItem>
                    ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>
      </Blade>

      <Alert>
        <Webhook className="h-4 w-4" />
        <AlertTitle>{t("eventSync.webhooks.title")}</AlertTitle>
        <AlertDescription>{t("eventSync.webhooks.body")}</AlertDescription>
      </Alert>

      <AlertDialog open={pendingDelete !== null} onOpenChange={(open) => !open && setPendingDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {t("eventSync.delete.title", {
                source: pendingDelete?.atsSource ?? pendingDelete?.id ?? "",
              })}
            </AlertDialogTitle>
            <AlertDialogDescription>{t("eventSync.delete.description")}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t("common.actions.cancel")}</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} disabled={isDeleting}>
              {isDeleting && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
              {t("eventSync.delete.confirm")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}
